import { useCallback, useEffect } from 'react';
import { useDocumentsList } from '@/core/api/query/getDocuments.query';
import { useMenus } from '@/core/api/query/menus.query';
import { useAuthStore } from '@/core/stores/auth.store';
import type { DocumentPropsTypes } from '@/core/types/documents';
import type { MenuItem } from '@/core/types/auth';
import { modals } from '@mantine/modals';
import { Text } from '@mantine/core';

const openLogoutConfirmationModal = (onConfirm: () => void) =>
    modals.openConfirmModal({
        title: 'Confirm Logout',
        centered: true,
        children: (
            <>
                <Text size="sm">
                    Are you sure you want to log out?
                </Text>
                <Text size="sm">
                    You will be redirected to the login page.
                </Text>
            </>
        ),
        labels: { confirm: 'Log out', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onCancel: () => null,
        onConfirm,
    });

const useAuth = () => {
    const authState = useAuthStore((state) => state.authState);
    const loading = useAuthStore((state) => state.loading);
    const hydrated = useAuthStore((state) => state.hydrated);
    const hydrate = useAuthStore((state) => state.hydrate);
    const login = useAuthStore((state) => state.login);
    const logout = useAuthStore((state) => state.logout);
    const syncDocuments = useAuthStore((state) => state.syncDocuments);
    const syncMenu = useAuthStore((state) => state.syncMenu);


    const { data: documents } = useDocumentsList(authState.user, authState.isAuthenticated);
    const { data: menus } = useMenus(authState.user);

    useEffect(() => {
        if (!hydrated) {
            void hydrate();
        }
    }, [hydrate, hydrated]);

    useEffect(() => {
        if (Array.isArray(documents)) {
            syncDocuments(documents as DocumentPropsTypes[]);
        }
    }, [documents, syncDocuments]);

    useEffect(() => {
        if (Array.isArray(menus)) {
            const convertMenuItem = (menu: { label: string; url: string | null; subMenu: typeof menus; icon: string | null; isReport: boolean | null }): MenuItem => ({
                label: menu.label,
                url: menu.url ?? '',
                subMenu: menu.subMenu.map(convertMenuItem),
                icon: menu.icon ?? undefined,
                isReport: menu.isReport ?? undefined,
            });
            const menuItems: MenuItem[] = menus.map(convertMenuItem);
            syncMenu(menuItems);
        }
    }, [menus, syncMenu]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const handleStorageChange = () => {
            void hydrate({ silent: true });
        };

        window.addEventListener('storage', handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [hydrate]);

    const requestLogout = useCallback(() => {
        openLogoutConfirmationModal(() => {
            void logout();
        });
    }, [logout]);

    return {
        authState,
        loading,
        login,
        logout: requestLogout,
    };
};

export { useAuthStore } from '@/core/stores/auth.store';
export default useAuth;
export type { User, MenuItem, AuthState } from '@/core/types/auth';
